import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import {
  selectHandRaiseStartTime,
  selectWorkstationStatus,
  selectHandRaisedForWorkstation
} from "~/features/andon/andon.selectors";
import { selectGridModeByIdMap } from "~/features/andon/andon.slice";
import {
  workstationFcAbbreviationSx,
  workstationLineTextSx,
  workstationLoadingSx,
  workstationNotFoundSx,
  workstationRolesSx,
  workstationTileSx
} from "~/features/andon/andon.styles";
import { AndonDisplayLine } from "~/features/andon/andon.types";
import {
  figureAndonColorFromStatus,
  getWorkstationModeIcon,
  buildHiddenAndonTooltip,
  formatAndonWorkstationRoles
} from "~/features/andon/andon.utils";
import { extractUserNameFromAuth0Id } from "~/lib/helpers";
import { getWorkstationModeDisplayName } from "~/lib/workstations.logic";
import { useGetWorkstationsQuery } from "~/redux/warehouse/workstation.hooks";
import { WorkstationSummaryDto } from "~/types/api";

type AndonBoardWorkstationCellProps = {
  workstationId: string;
  fontSize: string;
  iconScale?: number;
  displayLines?: AndonDisplayLine[];
  fcAbbreviation?: string;
  workstation?: WorkstationSummaryDto;
  workstationsReady?: boolean;
  onRemove?: () => void;
};

export function AndonBoardWorkstationCell({
  workstationId,
  fontSize,
  iconScale = 1.0,
  displayLines,
  fcAbbreviation,
  workstation: workstationProp,
  workstationsReady,
  onRemove
}: AndonBoardWorkstationCellProps) {
  const { t } = useTranslation();
  const { data: fetchedWorkstations } = useGetWorkstationsQuery(undefined, {
    skip: workstationsReady !== undefined
  });

  const workstationStatus = useAppSelector(
    selectWorkstationStatus(workstationId)
  );
  const gridSystemModeById = useAppSelector(selectGridModeByIdMap);
  const handRaiseStart = useAppSelector(
    selectHandRaiseStartTime(workstationId)
  );
  const handRaised = useAppSelector(
    selectHandRaisedForWorkstation(workstationId)
  );

  const [elapsedTime, setElapsedTime] = useState("");

  useEffect(() => {
    if (!handRaiseStart) {
      setElapsedTime("0s");
      return;
    }
    const update = () => {
      const elapsedMs = Date.now() - new Date(handRaiseStart).getTime();
      // Handle negative values (e.g., server/client time discrepancy)
      const totalSec = Math.max(0, Math.floor(elapsedMs / 1000));
      const hours = Math.floor(totalSec / 3600);
      const mins = Math.floor((totalSec % 3600) / 60);
      const secs = totalSec % 60;
      const parts = [
        hours > 0 ? `${hours}h` : "",
        hours > 0 || mins > 0 ? `${mins}m` : "",
        `${secs}s`
      ].filter(Boolean);
      setElapsedTime(parts.join(" "));
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [handRaiseStart]);

  const workstation =
    workstationsReady !== undefined
      ? workstationProp
      : fetchedWorkstations?.find((w) => w.id === workstationId);
  const workstationLoading =
    workstationsReady !== undefined
      ? !workstationsReady
      : fetchedWorkstations === undefined;

  if (workstationLoading) {
    return (
      <Box aria-label="loading workstation" sx={workstationLoadingSx}>
        <CircularProgress
          size={`calc(${fontSize} * 1.5)`}
          aria-hidden
          sx={{ color: "text.secondary" }}
        />
      </Box>
    );
  }

  if (!workstation) {
    return (
      <Box aria-label="workstation not found" sx={workstationNotFoundSx}>
        <WarningAmberIcon
          sx={{
            color: "#ef5350",
            fontSize: fontSize,
            minWidth: "1em"
          }}
        />
        <Typography
          noWrap
          sx={{
            width: "100%",
            textOverflow: "ellipsis",
            textAlign: "center",
            overflow: "hidden",
            whiteSpace: "nowrap",
            fontWeight: 500,
            color: "#c62828",
            fontSize: `calc(${fontSize} * 0.7)`,
            lineHeight: 1.2
          }}
        >
          {t("workstation not found")}
        </Typography>
        <Typography
          sx={{
            width: "100%",
            textAlign: "center",
            overflow: "hidden",
            fontWeight: 400,
            color: "#757575",
            fontSize: `calc(${fontSize} * 0.4)`,
            lineHeight: 1.1,
            wordBreak: "break-all"
          }}
        >
          {workstationId}
        </Typography>
        {onRemove && (
          <IconButton
            size="small"
            aria-label={t("remove from board")}
            onClick={(event) => {
              event.stopPropagation();
              onRemove();
            }}
            sx={{ color: "error.main" }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    );
  }

  const gridSystemMode =
    gridSystemModeById[workstation.autostoreGridId] ?? null;

  const {
    backgroundColor,
    borderColor,
    textColor,
    icon: StatusIcon,
    altTextKey
  } = figureAndonColorFromStatus({
    gridSystemMode,
    workstationStatus,
    workstationActive: workstation.status === "Active",
    handRaised: handRaised.raised,
    handRaiseRequestType: handRaised.requestType,
    workstationMode: workstation.mode
  });

  const translatedLabel = altTextKey ? t(altTextKey) : null;
  const displayMode = getWorkstationModeDisplayName(workstation.mode);
  const componentLabel = translatedLabel || displayMode;
  const ModeIcon =
    workstation.status === "Active" && workstation.mode
      ? getWorkstationModeIcon(workstation.mode)
      : null;
  const userName = extractUserNameFromAuth0Id(workstation.userId || "") || "";

  const statusText = handRaised.raised ? elapsedTime : componentLabel;

  const IconComponent = StatusIcon || ModeIcon;
  const rolesLabel = formatAndonWorkstationRoles(workstation.roles);

  const hiddenDetailsTooltip = buildHiddenAndonTooltip(
    displayLines,
    {
      iconLabel: componentLabel,
      statusText,
      deviceId: workstation.deviceId,
      userName: userName || undefined,
      fcAbbreviation,
      roles: rolesLabel
    },
    t
  );

  const baseIconSize = 1.5;
  const iconSize = `${baseIconSize * iconScale}em`;

  return (
    <Tooltip
      title={
        hiddenDetailsTooltip ? (
          <Box sx={{ whiteSpace: "pre-line" }}>{hiddenDetailsTooltip}</Box>
        ) : (
          ""
        )
      }
      disableHoverListener={!hiddenDetailsTooltip}
      disableFocusListener={!hiddenDetailsTooltip}
      disableTouchListener={!hiddenDetailsTooltip}
    >
      <Box
        aria-label={workstation.deviceId}
        sx={{
          ...workstationTileSx,
          backgroundColor,
          border: `1px solid ${borderColor}`
        }}
      >
        {displayLines?.includes("fcAbbreviation") && fcAbbreviation ? (
          <Typography
            data-testid="fc-abbreviation"
            aria-hidden
            sx={workstationFcAbbreviationSx(fontSize)}
          >
            {fcAbbreviation}
          </Typography>
        ) : null}
        {displayLines?.includes("roles") && rolesLabel ? (
          <Typography aria-hidden sx={workstationRolesSx(fontSize)}>
            {rolesLabel}
          </Typography>
        ) : null}
        {displayLines?.includes("icon") && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%"
            }}
          >
            {IconComponent ? (
              <IconComponent
                className="andon-icon"
                style={{
                  width: iconSize,
                  height: iconSize,
                  fill: textColor,
                  color: textColor,
                  flexShrink: 0
                }}
              />
            ) : (
              <Box sx={{ height: iconSize, width: iconSize }}></Box>
            )}
          </Box>
        )}

        {displayLines?.includes("status") && (
          <Typography
            data-testid={handRaised.raised ? "hand-raise-timer" : undefined}
            noWrap
            sx={{
              ...workstationLineTextSx,
              color: textColor,
              fontSize
            }}
          >
            {statusText || "\u00A0"}
          </Typography>
        )}

        {displayLines?.includes("deviceId") && (
          <Typography
            noWrap
            sx={{
              ...workstationLineTextSx,
              color: textColor,
              fontSize
            }}
          >
            {workstation.deviceId || "\u00A0"}
          </Typography>
        )}

        {displayLines?.includes("userName") && (
          <Typography
            noWrap
            sx={{
              ...workstationLineTextSx,
              fontStyle: "italic",
              color: textColor,
              fontSize
            }}
          >
            {userName || "\u00A0"}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}

export default AndonBoardWorkstationCell;
